import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './MobileCourseEditPage.css';
import { EndeavorApi } from '../../services/ApiService';

export default function MobileCourseEditPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    officeHours: '',
    professor: '',
    courseCode: '',
    endDate: '',
    startDate: '',
    semester: '',
    credits: ''
  });

  const handleBackClick = () => {
    navigate(`/m/course/${courseId}`);
  };

  // Load course data
  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await EndeavorApi.getEndeavor(courseId);
        if (response.ok) {
          const data = await response.json();
          setCourse(data);
          
          // Format dates for date input
          const formatDateForInput = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
          };
          
          setFormData({
            name: data.name || '',
            description: data.description || '',
            location: data.location || '',
            officeHours: data.officeHours || '',
            professor: data.professor || '',
            courseCode: data.courseCode || '',
            endDate: formatDateForInput(data.endDate),
            startDate: formatDateForInput(data.startDate),
            semester: data.semester || '',
            credits: data.credits || ''
          });
        } else {
          throw new Error('Failed to load course');
        }
      } catch (error) {
        console.error('Error loading course:', error);
        setError(error.message || 'Failed to load course');
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [courseId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Format dates for API
      const formatDateForAPI = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toISOString();
      };

      const updateData = {
        name: formData.name,
        description: formData.description,
        location: formData.location,
        officeHours: formData.officeHours,
        professor: formData.professor,
        courseCode: formData.courseCode,
        endDate: formatDateForAPI(formData.endDate),
        startDate: formatDateForAPI(formData.startDate),
        semester: formData.semester,
        credits: formData.credits ? parseInt(formData.credits) : null
      };

      const response = await EndeavorApi.updateEndeavor(courseId, updateData);
      
      if (response.ok) {
        // Navigate back to course details
        navigate(`/m/course/${courseId}`);
      } else {
        throw new Error('Failed to update course');
      }
    } catch (error) {
      console.error('Error saving course:', error);
      setError(error.message || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/m/course/${courseId}`);
  };

  if (loading) {
    return (
      <div className="m-course-edit-screen">
        <div className="m-course-edit-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Loading...</h1>
        </div>
        <div className="m-course-edit-loading">
          <div className="m-loading-spinner"></div>
          <p>Loading course...</p>
        </div>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="m-course-edit-screen">
        <div className="m-course-edit-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Error</h1>
        </div>
        <div className="m-course-edit-error">
          <p>Failed to load course: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="m-course-edit-screen">
      <div className="m-course-edit-header">
        <button className="m-back-btn" onClick={handleBackClick}>
          ← Back to Course
        </button>
        <h1>Edit Course</h1>
        <div className="m-header-spacer"></div>
      </div>

      <div className="m-course-edit-content">
        {error && (
          <div className="m-error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="m-course-edit-form">
          <div className="m-form-section">
            <h2 className="m-section-title">📚 Course Information</h2>
            
            <div className="m-form-group">
              <label htmlFor="name" className="m-form-label">Course Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="m-form-input"
                placeholder="Enter course name"
                required
              />
            </div>

            <div className="m-form-group">
              <label htmlFor="courseCode" className="m-form-label">Course Code</label>
              <input
                type="text"
                id="courseCode"
                name="courseCode"
                value={formData.courseCode}
                onChange={handleInputChange}
                className="m-form-input"
                placeholder="e.g., MGMT8133.01"
              />
            </div>

            <div className="m-form-row">
              <div className="m-form-group">
                <label htmlFor="semester" className="m-form-label">Semester</label>
                <select
                  id="semester"
                  name="semester"
                  value={formData.semester}
                  onChange={handleInputChange}
                  className="m-form-select"
                >
                  <option value="">Select semester</option>
                  <option value="Fall 2024">Fall 2024</option>
                  <option value="Spring 2025">Spring 2025</option>
                  <option value="Summer 2025">Summer 2025</option>
                  <option value="Fall 2025">Fall 2025</option>
                  <option value="Spring 2026">Spring 2026</option>
                </select>
              </div>

              <div className="m-form-group">
                <label htmlFor="credits" className="m-form-label">Credits</label>
                <input
                  type="number"
                  id="credits"
                  name="credits"
                  value={formData.credits}
                  onChange={handleInputChange}
                  className="m-form-input"
                  placeholder="3"
                  min="0"
                  max="12"
                />
              </div>
            </div>

            <div className="m-form-group">
              <label htmlFor="description" className="m-form-label">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="m-form-textarea"
                placeholder="Enter course description..."
                rows={4}
              />
            </div>
          </div>

          <div className="m-form-section">
            <h2 className="m-section-title">👨‍🏫 Course Details</h2>
            
            <div className="m-form-group">
              <label htmlFor="professor" className="m-form-label">Professor</label>
              <input
                type="text"
                id="professor"
                name="professor"
                value={formData.professor}
                onChange={handleInputChange}
                className="m-form-input"
                placeholder="Professor name"
              />
            </div>

            <div className="m-form-group">
              <label htmlFor="location" className="m-form-label">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="m-form-input"
                placeholder="e.g., Fulton Hall 235"
              />
            </div>

            <div className="m-form-group">
              <label htmlFor="officeHours" className="m-form-label">Office Hours</label>
              <input
                type="text"
                id="officeHours"
                name="officeHours"
                value={formData.officeHours}
                onChange={handleInputChange}
                className="m-form-input"
                placeholder="e.g., Tuesdays, 10:30 - 12:30"
              />
            </div>

            <div className="m-form-row">
              <div className="m-form-group">
                <label htmlFor="startDate" className="m-form-label">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="m-form-input"
                />
              </div>

              <div className="m-form-group">
                <label htmlFor="endDate" className="m-form-label">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className="m-form-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="m-course-edit-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="m-cancel-btn"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="m-save-btn"
            disabled={saving || !formData.name.trim()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
